/**
 * Public Product Page
 *
 * Displays a single product with details, images, and add to cart functionality.
 * Accessible at /s/:storeSlug/p/:productSlug
 */

import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLanguageDirection, type LanguageCode } from '../../i18n/languages';
import StoreFooter from '../../components/storefront/StoreFooter';
import CookieConsent from '../../components/storefront/CookieConsent';

interface Store {
  id: string;
  name: string;
  slug: string;
  logo_url?: string;
  theme: StoreTheme;
  currency: string;
  locale: LanguageCode;
}

interface StoreTheme {
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  textColor: string;
}

interface Product {
  id: string;
  name: string;
  slug: string;
  description?: string;
  short_description?: string;
  price: number;
  compare_at_price?: number;
  currency: string;
  images: string[];
  variants: ProductVariant[];
  options: ProductOption[];
  track_inventory: boolean;
  inventory_quantity: number;
  allow_backorders: boolean;
  requires_shipping: boolean;
  order_form_schema?: Record<string, unknown>;
  // Affiliate product fields
  product_type: 'physical' | 'digital' | 'service' | 'affiliate';
  affiliate_url?: string;
  affiliate_source?: string;
}

interface ProductVariant {
  id: string;
  name: string;
  price?: number;
  inventory_quantity?: number;
  sku?: string;
  options: Record<string, string>;
}

interface ProductOption {
  name: string;
  values: string[];
}

interface CartItem {
  productId: string;
  productName: string;
  variantId?: string;
  variantName?: string;
  price: number;
  quantity: number;
  image?: string;
}

const defaultTheme: StoreTheme = {
  primaryColor: '#0d9488',
  secondaryColor: '#6366f1',
  backgroundColor: '#ffffff',
  textColor: '#1f2937',
};

/**
 * Safely parse JSON from localStorage with fallback
 */
function safeParseJSON<T>(json: string | null, fallback: T): T {
  if (!json) return fallback;
  try {
    return JSON.parse(json) as T;
  } catch {
    console.warn('Failed to parse JSON from localStorage, using fallback');
    return fallback;
  }
}

export default function PublicProductPage() {
  const { storeSlug, productSlug } = useParams<{ storeSlug: string; productSlug: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('store');
  const [store, setStore] = useState<Store | null>(null);
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [redirectingToAffiliate, setRedirectingToAffiliate] = useState(false);
  const [showBrokenLinkModal, setShowBrokenLinkModal] = useState(false);
  const [reportingBrokenLink, setReportingBrokenLink] = useState(false);
  const [brokenLinkReported, setBrokenLinkReported] = useState(false);

  // Check if product is an affiliate product
  const isAffiliate = product?.product_type === 'affiliate';

  // Change language when store locale is loaded
  useEffect(() => {
    if (store?.locale && i18n.language !== store.locale) {
      i18n.changeLanguage(store.locale);
    }
  }, [store?.locale, i18n]);

  useEffect(() => {
    async function loadProduct() {
      try {
        setLoading(true);

        const response = await fetch(`/api/public/stores/${storeSlug}/products/${productSlug}`);
        if (!response.ok) {
          if (response.status === 404) {
            setError('Product not found');
          } else {
            setError('Failed to load product');
          }
          return;
        }

        const data = await response.json() as { store: Store; product: Product };
        setStore(data.store);
        setProduct(data.product);

        // Initialize selected options with first values
        if (data.product.options.length > 0) {
          const initialOptions: Record<string, string> = {};
          data.product.options.forEach((option) => {
            if (option.values.length > 0) {
              initialOptions[option.name] = option.values[0];
            }
          });
          setSelectedOptions(initialOptions);
        }

        // Load cart count
        const cartKey = `cart_${data.store.id}`;
        const cart = safeParseJSON<CartItem[]>(localStorage.getItem(cartKey), []);
        setCartCount(cart.reduce((sum, item) => sum + item.quantity, 0));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load product');
      } finally {
        setLoading(false);
      }
    }

    if (storeSlug && productSlug) {
      loadProduct();
    }
  }, [storeSlug, productSlug]);

  const theme = store?.theme || defaultTheme;

  // Find matching variant based on selected options
  // Handle case where variant.options might be undefined or missing keys
  const selectedVariant = product?.variants.find((variant) => {
    if (!variant.options) return false;
    return Object.entries(selectedOptions).every(
      ([key, value]) => variant.options[key] === value
    );
  });

  const currentPrice = selectedVariant?.price ?? product?.price ?? 0;
  const currentInventory = selectedVariant?.inventory_quantity ?? product?.inventory_quantity ?? 0;
  const isInStock = !product?.track_inventory || currentInventory > 0 || product?.allow_backorders;

  const storeLocale = store?.locale || 'en';
  const storeDirection = getLanguageDirection(storeLocale);

  const formatPrice = (price: number, currency: string) => {
    const localeMap: Record<string, string> = {
      en: 'en-US', zh: 'zh-CN', hi: 'hi-IN', es: 'es-ES', fr: 'fr-FR',
      ar: 'ar-SA', bn: 'bn-BD', pt: 'pt-BR', ru: 'ru-RU', ja: 'ja-JP',
      de: 'de-DE', ko: 'ko-KR', vi: 'vi-VN', tr: 'tr-TR', it: 'it-IT',
      th: 'th-TH', pl: 'pl-PL', nl: 'nl-NL', id: 'id-ID', he: 'he-IL',
    };
    const intlLocale = localeMap[storeLocale] || 'en-US';
    return new Intl.NumberFormat(intlLocale, {
      style: 'currency',
      currency: currency || 'USD',
    }).format(price / 100);
  };

  // Handle affiliate product click - track and redirect
  const handleAffiliateClick = async () => {
    if (!store || !product || !product.affiliate_url) return;

    setRedirectingToAffiliate(true);

    try {
      // Track the click (fire and forget)
      fetch(`/api/public/stores/${store.id}/products/${product.id}/affiliate-click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          referrer: document.referrer,
          user_agent: navigator.userAgent,
        }),
      }).catch(() => {
        // Ignore tracking errors - don't block the redirect
      });

      // Small delay to ensure tracking request is sent
      await new Promise(resolve => setTimeout(resolve, 100));

      // Redirect to affiliate URL
      window.open(product.affiliate_url, '_blank', 'noopener,noreferrer');
    } finally {
      setRedirectingToAffiliate(false);
    }
  };

  // Handle broken link report
  const handleReportBrokenLink = async () => {
    if (!store || !product) return;

    setReportingBrokenLink(true);

    try {
      await fetch(`/api/public/stores/${store.id}/products/${product.id}/report-broken-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          affiliate_url: product.affiliate_url,
          user_agent: navigator.userAgent,
          referrer: document.referrer,
        }),
      });

      setBrokenLinkReported(true);
    } catch {
      // Still show success to user even if report fails
      setBrokenLinkReported(true);
    } finally {
      setReportingBrokenLink(false);
    }
  };

  const handleAddToCart = async () => {
    if (!store || !product) return;

    // For affiliate products, redirect instead
    if (isAffiliate) {
      await handleAffiliateClick();
      return;
    }

    setAddingToCart(true);

    try {
      const cartKey = `cart_${store.id}`;
      const cart: CartItem[] = safeParseJSON<CartItem[]>(localStorage.getItem(cartKey), []);

      const newItem: CartItem = {
        productId: product.id,
        productName: product.name,
        variantId: selectedVariant?.id,
        variantName: selectedVariant?.name,
        price: currentPrice,
        quantity,
        image: product.images[0],
      };

      // Check if item already exists in cart
      const existingIndex = cart.findIndex(
        (item) => item.productId === newItem.productId && item.variantId === newItem.variantId
      );

      if (existingIndex >= 0) {
        cart[existingIndex].quantity += quantity;
      } else {
        cart.push(newItem);
      }

      localStorage.setItem(cartKey, JSON.stringify(cart));

      // Update cart count
      setCartCount(cart.reduce((sum, item) => sum + item.quantity, 0));

      setAddedToCart(true);
      setTimeout(() => setAddedToCart(false), 2000);
    } finally {
      setAddingToCart(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (error || !store || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('product.productNotFound')}</h1>
          <p className="text-gray-500 mb-6">{t('product.productNotFoundDescription')}</p>
          <Link to={`/s/${storeSlug}`} className="text-teal-600 hover:text-teal-700 font-medium">
            {t('product.backToStore')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" dir={storeDirection} lang={storeLocale}>
        {/* Header */}
        <header className="sticky top-0 z-50 bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to={`/s/${store.slug}`} className="flex items-center gap-3">
                {store.logo_url ? (
                  <img src={store.logo_url} alt={store.name} className="h-10 w-auto" />
                ) : (
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: theme.primaryColor }}
                  >
                    {store.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="text-xl font-bold">{store.name}</span>
              </Link>

              <Link
                to={`/s/${store.slug}/cart`}
                className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                {cartCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 w-5 h-5 text-xs text-white rounded-full flex items-center justify-center"
                    style={{ backgroundColor: theme.primaryColor }}
                  >
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </Link>
            </div>
          </div>
        </header>

        {/* Added to Cart Toast */}
        {addedToCart && (
          <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-4 duration-300">
            <div className="bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-medium">{t('product.addedToCart')}</span>
              <Link
                to={`/s/${store.slug}/cart`}
                className="text-white/80 hover:text-white text-sm underline"
              >
                {t('product.viewCart')}
              </Link>
            </div>
          </div>
        )}

        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center gap-2 text-sm text-gray-500">
            <Link to={`/s/${store.slug}`} className="hover:text-gray-700">
              {store.name}
            </Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-gray-900">{product.name}</span>
          </nav>
        </div>

        {/* Product Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Images */}
            <div className="space-y-4">
              {/* Main Image */}
              <div className="aspect-square bg-gray-100 rounded-xl overflow-hidden">
                {product.images.length > 0 ? (
                  <img
                    src={product.images[selectedImage]}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="w-24 h-24 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </div>

              {/* Thumbnails */}
              {product.images.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {product.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-colors ${
                        selectedImage === index ? 'border-teal-500' : 'border-transparent'
                      }`}
                    >
                      <img src={image} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="space-y-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{product.name}</h1>
                {product.short_description && (
                  <p className="mt-2 text-gray-500">{product.short_description}</p>
                )}
              </div>

              {/* Price */}
              <div className="flex items-center gap-3">
                <span className="text-3xl font-bold" style={{ color: theme.primaryColor }}>
                  {formatPrice(currentPrice, product.currency)}
                </span>
                {product.compare_at_price && product.compare_at_price > currentPrice && (
                  <span className="text-xl text-gray-400 line-through">
                    {formatPrice(product.compare_at_price, product.currency)}
                  </span>
                )}
              </div>

              {/* Options */}
              {product.options.length > 0 && (
                <div className="space-y-4">
                  {product.options.map((option) => (
                    <div key={option.name}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {option.name}
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {option.values.map((value) => (
                          <button
                            key={value}
                            onClick={() => setSelectedOptions({ ...selectedOptions, [option.name]: value })}
                            className={`px-4 py-2 border rounded-lg text-sm transition-all ${
                              selectedOptions[option.name] === value
                                ? 'border-teal-500 bg-teal-50 text-teal-700'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            {value}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Quantity - hide for affiliate products */}
              {!isAffiliate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('product.quantity')}
                  </label>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-10 h-10 border border-gray-200 rounded-lg flex items-center justify-center hover:bg-gray-50"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                      </svg>
                    </button>
                    <input
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-20 text-center border border-gray-200 rounded-lg py-2"
                    />
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-10 h-10 border border-gray-200 rounded-lg flex items-center justify-center hover:bg-gray-50"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              {/* Stock Status - hide for affiliate products */}
              {!isAffiliate && product.track_inventory && (
                <div className="flex items-center gap-2">
                  {isInStock ? (
                    <>
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-green-600">
                        {currentInventory > 0 ? t('product.xInStock', { count: currentInventory }) : t('product.availableOnBackorder')}
                      </span>
                    </>
                  ) : (
                    <>
                      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      <span className="text-sm text-red-600">{t('product.outOfStock')}</span>
                    </>
                  )}
                </div>
              )}

              {/* Affiliate Product Badge */}
              {isAffiliate && (
                <div className="flex items-center gap-2 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  <span className="text-sm text-purple-700 dark:text-purple-300">
                    {t('product.affiliateProductBadge')}
                  </span>
                </div>
              )}

              {/* Buy/Cart Buttons */}
              <div className="flex flex-col gap-2">
                {isAffiliate ? (
                  /* Affiliate Product: Single "Buy Now" button that redirects */
                  <>
                    <button
                      onClick={handleAffiliateClick}
                      disabled={redirectingToAffiliate || !product.affiliate_url}
                      className="w-full py-3 px-6 rounded-lg text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      style={{ backgroundColor: theme.primaryColor }}
                    >
                      {redirectingToAffiliate ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          {t('product.buyNow')}
                          <svg className="w-4 h-4 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                          </svg>
                        </>
                      )}
                    </button>
                    {/* Report broken link */}
                    <button
                      onClick={() => setShowBrokenLinkModal(true)}
                      className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors flex items-center justify-center gap-1"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      {t('product.reportBrokenLink')}
                    </button>
                  </>
                ) : (
                  /* Regular Product: Add to Cart + Buy Now buttons */
                  <>
                    <button
                      onClick={handleAddToCart}
                      disabled={!isInStock || addingToCart}
                      className="flex-1 py-3 px-6 rounded-lg text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      style={{ backgroundColor: addedToCart ? '#10b981' : theme.primaryColor }}
                    >
                      {addingToCart ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : addedToCart ? (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {t('product.addedToCart')}
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                          </svg>
                          {t('product.addToCart')}
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        handleAddToCart();
                        setTimeout(() => navigate(`/s/${store.slug}/cart`), 500);
                      }}
                      disabled={!isInStock}
                      className="py-3 px-6 border border-gray-200 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('product.buyNow')}
                    </button>
                  </>
                )}
              </div>

              {/* Description */}
              {product.description && (
                <div className="border-t border-gray-200 pt-6">
                  <h2 className="text-lg font-semibold mb-3">{t('product.description')}</h2>
                  <div className="prose prose-sm text-gray-600">
                    {product.description}
                  </div>
                </div>
              )}

              {/* Shipping Info - hide for affiliate products */}
              {!isAffiliate && product.requires_shipping && (
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center gap-3 text-sm text-gray-500">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                    <span>{t('product.requiresShipping')}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>

        {/* Footer */}
        <StoreFooter store={store} direction={storeDirection} />

        {/* Cookie Consent */}
        <CookieConsent
          storeId={store.id}
          storeSlug={store.slug}
          primaryColor={theme.primaryColor}
        />

        {/* Broken Link Report Modal */}
        {showBrokenLinkModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md">
              <div className="p-6">
                {brokenLinkReported ? (
                  /* Success state */
                  <>
                    <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2">
                      {t('product.thankYou')}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 text-center mb-6">
                      {t('product.brokenLinkReportedDescription')}
                    </p>
                    <button
                      onClick={() => {
                        setShowBrokenLinkModal(false);
                        setBrokenLinkReported(false);
                      }}
                      className="w-full py-3 px-4 rounded-lg font-medium transition-colors"
                      style={{ backgroundColor: theme.primaryColor, color: 'white' }}
                    >
                      {t('product.close')}
                    </button>
                  </>
                ) : (
                  /* Report form */
                  <>
                    <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2">
                      {t('product.reportBrokenLinkTitle')}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 text-center mb-6">
                      {t('product.reportBrokenLinkDescription')}
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowBrokenLinkModal(false)}
                        className="flex-1 py-3 px-4 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        {t('product.cancel')}
                      </button>
                      <button
                        onClick={handleReportBrokenLink}
                        disabled={reportingBrokenLink}
                        className="flex-1 py-3 px-4 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {reportingBrokenLink ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          t('product.submitReport')
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
  );
}
